
class ContinuousLogoSlider {
    constructor(containerSelector, options = {}) {
        // Configuration
        this.config = {
            speed: options.speed || 'normal', // 'slow', 'normal', 'fast'
            direction: options.direction || 'left', // 'left' or 'right'
            pauseOnHover: options.pauseOnHover !== undefined ? options.pauseOnHover : true,
            gap: options.gap || 40, // Gap between logos in pixels
            duplicates: options.duplicates || 3, // Number of times to duplicate the logo set
            ...options
        };

        // Find container and slider
        this.container = document.querySelector(containerSelector);
        if (!this.container) {
            console.error(`Container not found: ${containerSelector}`);
            return;
        }

        this.slider = this.container.querySelector('.logo-slider');
        if (!this.slider) {
            console.error('Logo slider element not found');
            return;
        }

        this.originalLogos = [];
        this.totalWidth = 0;
        this.init();
    }

    init() {
        // Store original logos
        this.storeOriginalLogos();
        
        // Duplicate logos for seamless scrolling
        this.duplicateLogos();
        
        // Apply speed and direction
        this.setSpeed();
        this.setDirection();
        
        // Setup hover behavior
        this.setupHoverBehavior();
        
        // Start animation after images load
        this.startAnimation();
    }

    storeOriginalLogos() {
        const logos = this.slider.querySelectorAll('.logo-img');
        logos.forEach(logo => {
            this.originalLogos.push({
                src: logo.src,
                alt: logo.alt,
                className: logo.className
            });
        });
    }

    duplicateLogos() {
        // Mark original logos
        const originalLogos = this.slider.querySelectorAll('.logo-img');
        originalLogos.forEach(logo => {
            logo.classList.add('original');
        });

        // Create multiple sets of duplicates for truly seamless scrolling
        const fragment = document.createDocumentFragment();
        
        // Add multiple duplicate sets
        for (let set = 0; set < this.config.duplicates; set++) {
            this.originalLogos.forEach(logoData => {
                const img = document.createElement('img');
                img.src = logoData.src;
                img.alt = logoData.alt;
                img.className = logoData.className + ' duplicate';
                img.style.margin = `0 ${this.config.gap}px`;
                fragment.appendChild(img);
            });
        }
        
        this.slider.appendChild(fragment);
    }

    startAnimation() {
        // Ensure images are loaded before calculating width
        const images = this.slider.querySelectorAll('.logo-img');
        let loadedCount = 0;
        
        const checkAllLoaded = () => {
            loadedCount++;
            if (loadedCount === images.length) {
                this.calculateAndAnimate();
            }
        };

        images.forEach(img => {
            if (img.complete) {
                checkAllLoaded();
            } else {
                img.addEventListener('load', checkAllLoaded);
                img.addEventListener('error', checkAllLoaded); // Handle failed loads
            }
        });

        // Fallback in case images are already loaded or fail
        setTimeout(() => {
            if (!this.slider.classList.contains('animate')) {
                this.calculateAndAnimate();
            }
        }, 500);
    }

    calculateAndAnimate() {
        // Calculate the width of one complete set of logos
        const originalLogos = this.slider.querySelectorAll('.logo-img.original');
        let setWidth = 0;
        
        originalLogos.forEach(logo => {
            const computedStyle = window.getComputedStyle(logo);
            const marginLeft = parseFloat(computedStyle.marginLeft) || 0;
            const marginRight = parseFloat(computedStyle.marginRight) || 0;
            setWidth += logo.offsetWidth + marginLeft + marginRight;
        });

        // Set CSS custom properties for the animation
        this.slider.style.setProperty('--scroll-width', `-${setWidth}px`);
        
        // Set animation duration based on speed
        let duration = 30;
        if (this.config.speed === 'slow') duration = 50;
        if (this.config.speed === 'fast') duration = 20;
        this.slider.style.setProperty('--duration', `${duration}s`);
        
        // Add the animate class to start the animation
        this.slider.classList.add('animate');
        
        // Store for reference
        this.totalWidth = setWidth;
    }

    setSpeed() {
        this.container.classList.remove('slow', 'fast');
        
        if (this.config.speed === 'slow') {
            this.container.classList.add('slow');
        } else if (this.config.speed === 'fast') {
            this.container.classList.add('fast');
        }
    }

    setDirection() {
        if (this.config.direction === 'right') {
            this.container.classList.add('reverse');
        } else {
            this.container.classList.remove('reverse');
        }
    }

    setupHoverBehavior() {
        if (!this.config.pauseOnHover) {
            // Override CSS to disable pause on hover
            const styleId = 'no-pause-on-hover';
            if (!document.getElementById(styleId)) {
                const style = document.createElement('style');
                style.id = styleId;
                style.textContent = `
                    .logo-slider-container:hover .logo-slider.animate {
                        animation-play-state: running !important;
                    }
                `;
                document.head.appendChild(style);
            }
        }
    }

    // Public API methods
    pause() {
        this.slider.style.animationPlayState = 'paused';
    }

    play() {
        this.slider.style.animationPlayState = 'running';
    }

    setOptions(options) {
        // Update config
        this.config = { ...this.config, ...options };
        
        // Restart with new options
        this.restart();
    }

    updateSpeed(speed) {
        this.config.speed = speed;
        this.setSpeed();
        
        // Update duration
        let duration = 30;
        if (speed === 'slow') duration = 50;
        if (speed === 'fast') duration = 20;
        this.slider.style.setProperty('--duration', `${duration}s`);
    }

    updateDirection(direction) {
        this.config.direction = direction;
        this.setDirection();
    }

    restart() {
        // Remove animation class
        this.slider.classList.remove('animate');
        
        // Remove duplicates
        const duplicates = this.slider.querySelectorAll('.duplicate');
        duplicates.forEach(dup => dup.remove());
        
        // Reinitialize
        setTimeout(() => {
            this.duplicateLogos();
            this.setSpeed();
            this.setDirection();
            this.startAnimation();
        }, 10);
    }

    destroy() {
        // Remove duplicates
        const duplicates = this.slider.querySelectorAll('.duplicate');
        duplicates.forEach(dup => dup.remove());
        
        // Remove animation
        this.slider.classList.remove('animate');
        this.slider.style.removeProperty('--scroll-width');
        this.slider.style.removeProperty('--duration');
        
        // Remove original class markers
        const originals = this.slider.querySelectorAll('.original');
        originals.forEach(logo => logo.classList.remove('original'));
    }
}

// The BlueTasks build wraps this file in a module closure, which scopes the
// class locally and tree-shakes it out of the bundle (nothing references it
// inside the module). Attaching it to the global scope keeps it in the output
// and makes it reachable from the inline script in Partners.cshtml.
if (typeof window !== 'undefined') {
    window.ContinuousLogoSlider = ContinuousLogoSlider;
}

